import type { HcpMentionSendOptions, SelectedHcpMention } from "../types/hcpMention";
import {
  CHAT_MESSAGE_BODY_MIN_LENGTH,
  CHAT_MESSAGE_INCOMPLETE_MESSAGE,
  isMeaningfulUserText,
} from "./openTextAnswerValidation";

export type ActiveHcpMention = {
  start: number;
  query: string;
};

/**
 * Active `@` query is the text from the nearest `@` (start of string or after
 * a non-word character) to the cursor. Spaces are allowed so names can be typed.
 */
export function parseActiveHcpMention(
  text: string,
  cursor: number,
  completedMentions: SelectedHcpMention[] = [],
): ActiveHcpMention | null {
  const safeCursor = Math.max(0, Math.min(cursor, text.length));
  const before = text.slice(0, safeCursor);
  const atIndex = before.lastIndexOf("@");
  if (atIndex < 0) return null;

  const charBeforeAt = atIndex === 0 ? "" : before[atIndex - 1];
  if (charBeforeAt && /[\w.]/.test(charBeforeAt)) return null;

  const query = before.slice(atIndex + 1);
  if (query.includes("\n") || query.length > 80) return null;

  const afterAt = before.slice(atIndex);
  const isCompletedMention = completedMentions.some((mention) => {
    const token = `@${mention.label}`;
    if (!afterAt.startsWith(token)) return false;
    const nextChar = afterAt[token.length];
    return nextChar === undefined || nextChar === " ";
  });
  if (isCompletedMention) return null;

  return { start: atIndex, query };
}

export function insertHcpMention(
  text: string,
  mentionStart: number,
  cursor: number,
  label: string,
): { text: string; cursor: number } {
  const mentionText = label.startsWith("@") ? label : `@${label}`;
  const next = `${text.slice(0, mentionStart)}${mentionText} ${text.slice(cursor)}`;
  return {
    text: next,
    cursor: mentionStart + mentionText.length + 1,
  };
}

export function applyPromptPreservingMentions(
  text: string,
  prompt: string,
  mentions: SelectedHcpMention[],
): string {
  const trimmedPrompt = prompt.trim();
  const prefixes = mentions
    .map((mention) => {
      const token = mention.label.startsWith("@")
        ? mention.label
        : `@${mention.label}`;
      return { token, index: text.indexOf(token) };
    })
    .filter((item) => item.index >= 0)
    .sort((a, b) => a.index - b.index)
    .filter(
      (item, index, all) =>
        all.findIndex((other) => other.token === item.token) === index,
    )
    .map((item) => item.token);

  const withTrailingSpace = (value: string) =>
    value.endsWith(" ") ? value : `${value} `;

  if (prefixes.length === 0) return withTrailingSpace(trimmedPrompt);
  return withTrailingSpace(`${prefixes.join(" ")} ${trimmedPrompt}`);
}

export function mentionTokensStillInText(
  text: string,
  mentions: SelectedHcpMention[],
): string[] {
  const tokens: string[] = [];
  for (const mention of mentions) {
    if (
      text.includes(`@${mention.label}`) &&
      !tokens.includes(mention.mentionToken)
    ) {
      tokens.push(mention.mentionToken);
    }
  }
  return tokens;
}

export function mergeMentionsFromResults(
  text: string,
  selected: SelectedHcpMention[],
  results: SelectedHcpMention[],
): SelectedHcpMention[] {
  const merged = [...selected];
  for (const result of results) {
    if (
      text.includes(`@${result.label}`) &&
      !merged.some((item) => item.mentionToken === result.mentionToken)
    ) {
      merged.push({
        mentionToken: result.mentionToken,
        label: result.label,
      });
    }
  }
  return merged;
}

export function toHcpMentionSendOptions(
  text: string,
  mentions: SelectedHcpMention[],
): HcpMentionSendOptions | undefined {
  const mentionTokens = mentionTokensStillInText(text, mentions);
  if (mentionTokens.length === 0) return undefined;

  return {
    mentionToken: mentionTokens[0],
    mentionTokens,
  };
}

//Text left after removing `@` HCP mention tokens (whitespace-normalized).
export function stripHcpMentionsFromText(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return "";

  const ranges = findHcpMentionRanges(trimmed);
  if (ranges.length === 0) return trimmed;

  let remainder = "";
  let lastEnd = 0;
  for (const { start, end } of ranges) {
    remainder += trimmed.slice(lastEnd, start);
    lastEnd = end;
  }
  remainder += trimmed.slice(lastEnd);
  return remainder.replace(/\s+/g, " ").trim();
}

export function hasSendableTextBeyondHcpMentions(text: string): boolean {
  return isMeaningfulUserText(
    stripHcpMentionsFromText(text),
    CHAT_MESSAGE_BODY_MIN_LENGTH,
  );
}

export function getChatSendValidationMessage(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed || hasSendableTextBeyondHcpMentions(trimmed)) return null;
  return CHAT_MESSAGE_INCOMPLETE_MESSAGE;
}

const labelByToken = new Map<string, string>();

export function rememberHcpSearchLabels(
  results: Array<{ mentionToken: string; label: string }>,
) {
  for (const result of results) {
    if (result.mentionToken && result.label) {
      labelByToken.set(result.mentionToken, result.label);
    }
  }
}

export function hcpLabelsForTokens(tokens: string[]): string[] {
  const labels: string[] = [];
  for (const token of tokens) {
    const label = labelByToken.get(token);
    if (label && !labels.includes(label)) labels.push(label);
  }
  return labels;
}

const NAME_CONTINUATION = /^[A-Z\u00C0-\u024F\u0600-\u06FF]/;

function nextCharOk(text: string, index: number): boolean {
  const next = text[index];
  return next === undefined || /[\s.,!?;:]/.test(next);
}

function rangesFromLabels(
  text: string,
  labels: string[],
): Array<{ start: number; end: number }> {
  const needles = [
    ...new Set(
      labels
        .map((label) => (label.startsWith("@") ? label : `@${label}`).trim())
        .filter(Boolean),
    ),
  ].sort((a, b) => b.length - a.length);

  const taken = new Array(text.length).fill(false);
  const ranges: Array<{ start: number; end: number }> = [];

  for (const needle of needles) {
    let from = 0;
    while (from <= text.length - needle.length) {
      const start = text.indexOf(needle, from);
      if (start < 0) break;
      const end = start + needle.length;
      const prevOk = start === 0 || !/[\w.]/.test(text[start - 1]);
      const overlaps = taken.slice(start, end).some(Boolean);
      if (prevOk && !overlaps && nextCharOk(text, end)) {
        ranges.push({ start, end });
        for (let i = start; i < end; i += 1) taken[i] = true;
      }
      from = start + 1;
    }
  }

  return ranges.sort((a, b) => a.start - b.start);
}

function rangesFromText(text: string): Array<{ start: number; end: number }> {
  const ranges: Array<{ start: number; end: number }> = [];
  let i = 0;

  while (i < text.length) {
    if (text[i] !== "@" || (i > 0 && /[\w.]/.test(text[i - 1]))) {
      i += 1;
      continue;
    }

    const after = text.slice(i + 1);
    const words = [...after.matchAll(/[^\s@]+/g)];
    if (words.length === 0) {
      i += 1;
      continue;
    }

    let count = 1;
    for (let w = 1; w < words.length; w += 1) {
      const prev = words[w - 1];
      const gapStart = (prev.index ?? 0) + prev[0].length;
      const gap = after.slice(gapStart, words[w].index);
      if (gap !== " " || !NAME_CONTINUATION.test(words[w][0])) break;
      count += 1;
    }

    const following = words[count];
    if (
      count >= 3 &&
      following &&
      !NAME_CONTINUATION.test(following[0])
    ) {
      count -= 1;
    }

    const last = words[count - 1];
    const end = i + 1 + (last.index ?? 0) + last[0].length;
    ranges.push({ start: i, end });
    i = end;
  }

  return ranges;
}

export function findHcpMentionRanges(
  text: string,
  labels: string[] = [],
): Array<{ start: number; end: number }> {
  if (labels.length > 0) return rangesFromLabels(text, labels);
  return rangesFromText(text);
}
